import React, { createContext, useContext, useRef, useState, useCallback, useMemo, useEffect } from 'react';

// Split contexts: state changes rarely; time ticks every second.
// Cards subscribe to state only, so playback ticks don't re-render the whole page.
const AudioStateContext = createContext(null);
const AudioTimeContext = createContext(null);

export const AudioPlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [volume, setVolumeState] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const lastTickRef = useRef(0);

  const playEpisode = useCallback((episode) => {
    const audio = audioRef.current;
    if (!audio || !episode?.audioUrl) return;

    setError(false);

    if (currentEpisode?.id === episode.id) {
      if (audio.paused) {
        audio.play().catch((e) => {
          if (e.name !== 'AbortError') setError(true);
        });
      } else {
        audio.pause();
      }
      return;
    }

    setCurrentEpisode(episode);
    setCurrentTime(0);
    setDuration(episode.durationSec || 0);
    setIsLoading(true);
    audio.src = episode.audioUrl;
    audio.play().catch((e) => {
      if (e.name !== 'AbortError') {
        setError(true);
        setIsLoading(false);
      }
    });
  }, [currentEpisode]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !currentEpisode) return;
    if (audio.paused) {
      audio.play().catch((e) => {
        if (e.name !== 'AbortError') setError(true);
      });
    } else {
      audio.pause();
    }
  }, [currentEpisode]);

  const seek = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio || !audio.src) return;
    audio.currentTime = seconds;
    setCurrentTime(seconds);
  }, []);

  const skip = useCallback((delta) => {
    const audio = audioRef.current;
    if (!audio || !audio.src) return;
    const max = audio.duration && isFinite(audio.duration) ? audio.duration : Infinity;
    audio.currentTime = Math.min(Math.max(audio.currentTime + delta, 0), max);
  }, []);

  const setVolume = useCallback((v) => {
    if (audioRef.current) audioRef.current.volume = v;
    setVolumeState(v);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      if (audioRef.current) audioRef.current.muted = !prev;
      return !prev;
    });
  }, []);

  const closePlayer = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    setCurrentEpisode(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(false);
  }, []);

  const handleTimeUpdate = (e) => {
    const t = e.target.currentTime;
    if (Math.floor(t) !== lastTickRef.current) {
      lastTickRef.current = Math.floor(t);
      setCurrentTime(t);
    }
  };

  const handleLoadedMetadata = (e) => {
    setIsLoading(false);
    if (e.target.duration && isFinite(e.target.duration)) {
      setDuration(e.target.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  // Media Session: lock-screen / media-key metadata and controls
  useEffect(() => {
    if (!('mediaSession' in navigator) || !currentEpisode) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentEpisode.title,
      artist: 'Namaskar India',
      artwork: currentEpisode.imageUrl
        ? [{ src: currentEpisode.imageUrl, sizes: '512x512', type: 'image/jpeg' }]
        : [],
    });
    navigator.mediaSession.setActionHandler('play', () => audioRef.current?.play().catch(() => {}));
    navigator.mediaSession.setActionHandler('pause', () => audioRef.current?.pause());
    navigator.mediaSession.setActionHandler('seekbackward', () => skip(-10));
    navigator.mediaSession.setActionHandler('seekforward', () => skip(10));
    navigator.mediaSession.setActionHandler('seekto', (d) => { if (d.seekTime != null) seek(d.seekTime); });
  }, [currentEpisode, seek, skip]);

  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = currentEpisode ? (isPlaying ? 'playing' : 'paused') : 'none';
    }
  }, [isPlaying, currentEpisode]);

  const stateValue = useMemo(() => ({
    currentEpisode,
    isPlaying,
    isLoading,
    error,
    volume,
    isMuted,
    playEpisode,
    togglePlay,
    seek,
    skip,
    setVolume,
    toggleMute,
    closePlayer,
  }), [currentEpisode, isPlaying, isLoading, error, volume, isMuted,
       playEpisode, togglePlay, seek, skip, setVolume, toggleMute, closePlayer]);

  const timeValue = useMemo(() => ({ currentTime, duration }), [currentTime, duration]);

  return (
    <AudioStateContext.Provider value={stateValue}>
      <AudioTimeContext.Provider value={timeValue}>
        {children}
        <audio
          ref={audioRef}
          preload="none"
          onPlay={() => { setIsPlaying(true); setIsLoading(false); }}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onError={() => { setError(true); setIsPlaying(false); setIsLoading(false); }}
        />
      </AudioTimeContext.Provider>
    </AudioStateContext.Provider>
  );
};

export const useAudioPlayer = () => {
  const ctx = useContext(AudioStateContext);
  if (!ctx) throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  return ctx;
};

export const useAudioTime = () => {
  const ctx = useContext(AudioTimeContext);
  if (!ctx) throw new Error('useAudioTime must be used within AudioPlayerProvider');
  return ctx;
};
